import { useMutation, useApolloClient, gql } from '@apollo/client';
import { transformError } from '@core/adapters/errors/transformError';
import { track } from '@core/logging';
import { EXTENDED_GRAPHQL_TIMEOUT_MS } from '@core/services/constants';
import type { GraphQLFormattedError } from 'graphql';
import { useMemo, useCallback } from 'react';

import { type SelectAuditDecisionResponse, type SelectAuditDecisionVariables, type GraphQLAuditItemWithDecisions } from '../domain/graphql-types';
import { SELECT_AUDIT_DECISION } from '../graphql/mutations/selectAuditDecision';
import {
  updateAuditItemInCache,
  createOptimisticSelectResponse,
  updateDecisionsInCache,
  updateSelectionStateInCache,
  rollbackSelectionStateInCache,
} from '../utils/auditCacheUpdates';

/**
 * Hook for selecting audit decisions
 * Enhanced with adapter pattern for better error handling and separation of concerns
 * Includes strategic business event tracking for observability compliance
 */
export function useSelectAuditDecision() {
  const client = useApolloClient();
  const [selectAuditDecisionMutation, { data, loading, error }] = useMutation<SelectAuditDecisionResponse, SelectAuditDecisionVariables>(
    SELECT_AUDIT_DECISION,
    {
      errorPolicy: 'all',
      context: { timeout: EXTENDED_GRAPHQL_TIMEOUT_MS },
      update: (cache, { data: mutationData }) => {
        // Only update cache if the mutation was successful
        if (mutationData?.selectAuditDecision) {
          const { auditItem } = mutationData.selectAuditDecision;

          // Update audit item and decisions list to reflect selection changes
          updateAuditItemInCache(cache, auditItem);
          updateDecisionsInCache(cache, auditItem);
        }
      },
    }
  );

  // Use GraphQL data directly
  const updatedAuditItem = useMemo(() => {
    if (!data?.selectAuditDecision) {
      return undefined;
    }

    return data.selectAuditDecision.auditItem;
  }, [data]);

  // Transform errors using simplified adapter pattern - return array for consistency
  const domainErrors = useMemo(() => {
    if (!error) return [];

    // Map GraphQL errors to user-friendly format
    const mappedError = transformError(error);

    // Return error array for UI handling
    return [mappedError];
  }, [error]);

  const handleSuccessfulSelection = useCallback((input: SelectAuditDecisionVariables['input'], auditItemData: GraphQLAuditItemWithDecisions) => {
    track('SelectAuditDecisionSucceeded', {
      decisionId: input.decisionId,
      auditItemId: input.auditItemId,
      auditSessionId: input.auditSessionId,
      selectedDecisionId: auditItemData.selectedDecisionId || 'none',
      totalDecisionsCount: auditItemData.decisionsCount.toString(),
    });

    return {
      success: true,
      data: auditItemData,
    };
  }, []);

  const handleSelectionError = useCallback((input: SelectAuditDecisionVariables['input'], errors: readonly GraphQLFormattedError[]) => {
    const mappedErrors = errors.map(transformError);
    const firstError = mappedErrors[0];

    track('SelectAuditDecisionFailed', {
      decisionId: input.decisionId,
      auditItemId: input.auditItemId,
      auditSessionId: input.auditSessionId,
      errorType: 'api_error',
      errorMessage: firstError?.message || 'Unknown error',
    });

    return {
      success: false,
      errors: mappedErrors,
    };
  }, []);

  const selectAuditDecision = useCallback(
    // eslint-disable-next-line complexity
    async (variables: SelectAuditDecisionVariables) => {
      const { input } = variables;

      track('SelectAuditDecisionInitiated', {
        decisionId: input.decisionId,
        auditItemId: input.auditItemId,
        auditSessionId: input.auditSessionId,
      });

      // Get current selection state for rollback if needed
      const currentAuditItem = client.cache.readFragment<{ selectedDecisionId?: string }>({
        id: client.cache.identify({ __typename: 'AuditItem', id: input.auditItemId }),
        fragment: gql`
          fragment CurrentSelectionState on AuditItem {
            selectedDecisionId
          }
        `,
      });
      const previousSelectedDecisionId = currentAuditItem?.selectedDecisionId || null;

      try {
        // Create optimistic response for immediate UI feedback
        const optimisticResponse = createOptimisticSelectResponse(client.cache, input.auditItemId, input.decisionId);

        // Immediately update selection state in cache for instant UI feedback
        updateSelectionStateInCache(client.cache, input.auditItemId, input.decisionId);

        const result = await selectAuditDecisionMutation({
          variables,
          ...(optimisticResponse && { optimisticResponse }),
          update: (cache, { data: mutationData }) => {
            // Only run this update for successful mutations
            if (mutationData?.selectAuditDecision) {
              const { auditItem } = mutationData.selectAuditDecision;

              // Update audit item and decisions list to reflect selection changes
              updateAuditItemInCache(cache, auditItem);
              updateDecisionsInCache(cache, auditItem);
            }
          },
        });

        if (result.data?.selectAuditDecision && !result.errors) {
          return handleSuccessfulSelection(input, result.data.selectAuditDecision.auditItem);
        }

        // Rollback optimistic changes on error
        rollbackSelectionStateInCache(client.cache, input.auditItemId, previousSelectedDecisionId);

        return handleSelectionError(input, result.errors || ([] as readonly GraphQLFormattedError[]));
      } catch (error) {
        // Rollback optimistic changes on unexpected error
        rollbackSelectionStateInCache(client.cache, input.auditItemId, previousSelectedDecisionId);

        track('SelectAuditDecisionFailed', {
          decisionId: input.decisionId,
          auditItemId: input.auditItemId,
          auditSessionId: input.auditSessionId,
          errorType: error instanceof Error ? error.name : 'unknown',
          errorMessage: error instanceof Error ? error.message : 'Unexpected error',
        });

        throw error;
      }
    },
    [selectAuditDecisionMutation, handleSuccessfulSelection, handleSelectionError, client.cache]
  );

  return {
    selectAuditDecision,
    updatedAuditItem,
    loading,
    errors: domainErrors,
  };
}
